import type { Data } from '@dnd-kit/abstract'
import { SortableDraggable } from '@dnd-kit/dom/sortable'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'

import { Flex } from '@/components/ui/flex'

import { BlockCard } from '../block-card'
import { SortableBlockItem } from '../block-item'
import { HomepageService } from '../home-screen.types'

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>

interface ServicesBlockProps {
  services: HomepageService[]
  disabled: boolean
  // The Services widget itself is off - the list stays visible (grayed out) instead of
  // disappearing, but nothing in it can be toggled or dragged.
  locked: boolean
  onToggle: (bookingType: number) => void
  onReorder: (sourceIndex: number, targetIndex: number) => void
}

export const ServicesBlock = ({
  services,
  disabled,
  locked,
  onToggle,
  onReorder,
}: ServicesBlockProps) => {
  const { t } = useTranslation(['home-screen'])

  const handleDragEnd: DragEndHandler = (event) => {
    if (locked) return
    const { canceled, operation } = event
    if (canceled) return

    const { source } = operation
    if (!source || !isSortable(source)) return

    const sortableSource = source as SortableDraggable<Data>
    const sourceIndex = sortableSource.initialIndex
    const targetIndex = sortableSource.index
    if (sourceIndex === targetIndex) return

    onReorder(sourceIndex, targetIndex)
  }

  return (
    <BlockCard
      locked={locked}
      title={t('home-screen:labels.servicesConfigurationHeader')}
      tooltip={t('home-screen:tooltips.servicesConfiguration')}
    >
      <Flex direction="column" gap={8}>
        <DragDropProvider onDragEnd={handleDragEnd}>
          {services.map((service, index) => (
            <SortableBlockItem
              key={service.bookingType}
              checked
              disabled={disabled}
              id={`service-${service.bookingType}`}
              index={index}
              label={
                service.data?.name ||
                t('home-screen:labels.serviceFallbackName', { bookingType: service.bookingType })
              }
              locked={locked}
              onToggle={() => onToggle(service.bookingType)}
            />
          ))}
        </DragDropProvider>
      </Flex>
    </BlockCard>
  )
}
